import React, { useState } from 'react';
import { 
  XMarkIcon, 
  ClipboardDocumentIcon,
  CheckIcon,
  ShareIcon,
  GlobeAltIcon,
  LockClosedIcon,
  CodeBracketIcon
} from '@heroicons/react/24/outline';
import { FormTemplate } from '../../types/formBuilder';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCustomers } from '../../hooks/useCustomers';
import Modal from '../common/Modal';
import { toast } from 'react-hot-toast';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: FormTemplate;
  onUpdateTemplate: (updates: Partial<FormTemplate>) => void;
}

const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  template,
  onUpdateTemplate
}) => {
  const { t } = useLanguage();
  const { customers } = useCustomers();
  const [copied, setCopied] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState<'link' | 'embed'>('link');
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');

  // Generate public ID if not exists
  const generatePublicId = () => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  };

  // Get or create public settings
  const publicSettings = template.publicSettings || {
    isPublic: false,
    publicId: generatePublicId(),
    allowAnonymous: true,
    requiresEmail: false,
    maxSubmissions: undefined,
    currentSubmissions: 0
  };

  const publicUrl = `${window.location.origin}/forms/public/${publicSettings.publicId}/fill`;
  const embedUrl = `${window.location.origin}/forms/embed/${publicSettings.publicId}`;
  const embedCode = `<iframe src="${embedUrl}" width="100%" height="600" frameborder="0" style="border: none; border-radius: 8px;"></iframe>`;

  const handleTogglePublic = async () => {
    setIsUpdating(true);
    try {
      const newPublicSettings = {
        ...publicSettings,
        isPublic: !publicSettings.isPublic,
        publicId: publicSettings.publicId || generatePublicId()
      };

      await onUpdateTemplate({
        publicSettings: newPublicSettings
      });

      toast.success(
        newPublicSettings.isPublic 
          ? (t('formBuilder.share.enabled') || 'Public sharing enabled')
          : (t('formBuilder.share.disabled') || 'Public sharing disabled')
      );
    } catch (error) {
      console.error('Error updating public settings:', error);
      toast.error(t('formBuilder.errors.updateFailed') || 'Failed to update settings');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      const textToCopy = activeTab === 'link' ? publicUrl : embedCode;
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      toast.success(t('common.copied') || 'Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error(t('common.copyFailed') || 'Failed to copy');
    }
  };

  const handleSettingsChange = async (key: keyof typeof publicSettings, value: any) => {
    setIsUpdating(true);
    try {
      const newPublicSettings = {
        ...publicSettings,
        [key]: value
      };

      await onUpdateTemplate({
        publicSettings: newPublicSettings
      });
    } catch (error) {
      console.error('Error updating setting:', error);
      toast.error(t('formBuilder.errors.updateFailed') || 'Failed to update settings');
    } finally {
      setIsUpdating(false);
    }
  };

  // Customer Portal Settings
  const customerPortalSettings = template.customerPortalSettings || {
    isAvailableOnPortal: false,
    allowAllCustomers: false,
    allowedCustomers: []
  };

  const handleTogglePortal = async (enabled: boolean) => {
    setIsUpdating(true);
    try {
      await onUpdateTemplate({
        customerPortalSettings: {
          ...customerPortalSettings,
          isAvailableOnPortal: enabled
        }
      });
      toast.success(
        enabled 
          ? 'Űrlap elérhetővé téve az ügyfélportálon'
          : 'Űrlap eltávolítva az ügyfélportálról'
      );
    } catch (error) {
      console.error('Error updating portal settings:', error);
      toast.error('Hiba a beállítások frissítésekor');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleAllCustomers = async (allowAll: boolean) => {
    setIsUpdating(true);
    try {
      await onUpdateTemplate({
        customerPortalSettings: {
          ...customerPortalSettings,
          allowAllCustomers: allowAll,
          allowedCustomers: allowAll ? [] : customerPortalSettings.allowedCustomers
        }
      });
    } catch (error) {
      console.error('Error updating customer settings:', error);
      toast.error('Hiba a beállítások frissítésekor');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleCustomer = async (customerId: string) => {
    setIsUpdating(true);
    try {
      const currentAllowed = customerPortalSettings.allowedCustomers || [];
      const isCurrentlyAllowed = currentAllowed.includes(customerId);
      
      await onUpdateTemplate({
        customerPortalSettings: {
          ...customerPortalSettings,
          allowedCustomers: isCurrentlyAllowed
            ? currentAllowed.filter(id => id !== customerId)
            : [...currentAllowed, customerId]
        }
      });
    } catch (error) {
      console.error('Error updating customer access:', error);
      toast.error('Hiba az ügyfél hozzáférés frissítésekor');
    } finally {
      setIsUpdating(false);
    }
  };

  const getCustomerName = (customer: any) => {
    if (customer.type === 'organization') {
      return customer.companyName;
    }
    return `${customer.lastName} ${customer.firstName}`;
  };

  const filteredCustomers = customers.filter(customer =>
    getCustomerName(customer).toLowerCase().includes(customerSearchTerm.toLowerCase())
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('formBuilder.share.title') || 'Share Form'} size="4xl">
      <div className="p-6">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center">
              <ShareIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {template.name}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('formBuilder.share.description') || 'Share this form with others'}
              </p>
            </div>
          </div>

        {/* Public Toggle */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {publicSettings.isPublic ? (
                <GlobeAltIcon className="h-5 w-5 text-green-600" />
              ) : (
                <LockClosedIcon className="h-5 w-5 text-gray-400" />
              )}
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  {t('formBuilder.share.publicAccess') || 'Public Access'}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {publicSettings.isPublic 
                    ? (t('formBuilder.share.publicEnabled') || 'Anyone with the link can fill this form')
                    : (t('formBuilder.share.publicDisabled') || 'Only authorized users can access this form')
                  }
                </p>
              </div>
            </div>
            <button
              onClick={handleTogglePublic}
              disabled={isUpdating}
              className={`
                relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                ${publicSettings.isPublic ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}
                ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <span
                className={`
                  pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                  transition duration-200 ease-in-out
                  ${publicSettings.isPublic ? 'translate-x-5' : 'translate-x-0'}
                `}
              />
            </button>
          </div>
        </div>

        {/* Tabs */}
        {publicSettings.isPublic && (
          <div className="space-y-4">
            {/* Tab Navigation */}
            <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
              <button
                onClick={() => setActiveTab('link')}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center space-x-2 ${
                  activeTab === 'link'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <ShareIcon className="h-4 w-4" />
                <span>Public Link</span>
              </button>
              <button
                onClick={() => setActiveTab('embed')}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center space-x-2 ${
                  activeTab === 'embed'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <CodeBracketIcon className="h-4 w-4" />
                <span>Embed Code</span>
              </button>
            </div>

            {/* Tab Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {activeTab === 'link' 
                  ? (t('formBuilder.share.publicLink') || 'Public Link')
                  : 'Embed Code'
                }
              </label>
              <div className="flex items-center space-x-2">
                <textarea
                  value={activeTab === 'link' ? publicUrl : embedCode}
                  readOnly
                  rows={activeTab === 'link' ? 1 : 3}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm resize-none"
                />
                <button
                  onClick={handleCopyLink}
                  className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm flex items-center space-x-1 transition-colors"
                >
                  {copied ? (
                    <CheckIcon className="h-4 w-4" />
                  ) : (
                    <ClipboardDocumentIcon className="h-4 w-4" />
                  )}
                  <span>{copied ? (t('common.copied') || 'Copied') : (t('common.copy') || 'Copy')}</span>
                </button>
              </div>
              
              {activeTab === 'embed' && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Copy this code and paste it into your website's HTML to embed the form.
                </p>
              )}
            </div>

            {/* Settings */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {t('formBuilder.share.settings') || 'Sharing Settings'}
              </h4>
              
              <div className="space-y-3">
                {/* Allow Anonymous */}
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm text-gray-700 dark:text-gray-300">
                      {t('formBuilder.share.allowAnonymous') || 'Allow anonymous submissions'}
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('formBuilder.share.allowAnonymousDesc') || 'Users can submit without providing their email'}
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={publicSettings.allowAnonymous}
                    onChange={(e) => handleSettingsChange('allowAnonymous', e.target.checked)}
                    disabled={isUpdating}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>

                {/* Require Email - Hidden until implemented */}
                {false && (
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm text-gray-700 dark:text-gray-300">
                        {t('formBuilder.share.requireEmail') || 'Require email address'}
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('formBuilder.share.requireEmailDesc') || 'Users must provide their email to submit'}
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={publicSettings.requiresEmail}
                      onChange={(e) => handleSettingsChange('requiresEmail', e.target.checked)}
                      disabled={isUpdating}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                )}

                {/* Max Submissions */}
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                    {t('formBuilder.share.maxSubmissions') || 'Maximum submissions (optional)'}
                  </label>
                  <input
                    type="number"
                    value={publicSettings.maxSubmissions || ''}
                    onChange={(e) => handleSettingsChange('maxSubmissions', e.target.value ? parseInt(e.target.value) : undefined)}
                    disabled={isUpdating}
                    placeholder={t('formBuilder.share.unlimited') || 'Unlimited'}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                  {publicSettings.maxSubmissions && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {publicSettings.currentSubmissions || 0} / {publicSettings.maxSubmissions} {t('formBuilder.share.submitted') || 'submitted'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Customer Portal Settings */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-800 p-4">
          <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-4 flex items-center">
            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Ügyfélportál Beállítások
          </h4>
          
          <div className="space-y-4">
            {/* Enable on Portal */}
            <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg">
              <div>
                <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  Elérhető az ügyfélportálon
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Az űrlap megjelenik az ügyfélportálon
                </p>
              </div>
              <button
                onClick={() => handleTogglePortal(!customerPortalSettings.isAvailableOnPortal)}
                disabled={isUpdating}
                className={`
                  relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                  transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                  ${customerPortalSettings.isAvailableOnPortal ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}
                  ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                <span
                  className={`
                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                    transition duration-200 ease-in-out
                    ${customerPortalSettings.isAvailableOnPortal ? 'translate-x-5' : 'translate-x-0'}
                  `}
                />
              </button>
            </div>

            {customerPortalSettings.isAvailableOnPortal && (
              <>
                {/* Allow All Customers */}
                <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg">
                  <div>
                    <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Minden ügyfél számára elérhető
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Az űrlap minden ügyfél számára látható lesz
                    </p>
                  </div>
                  <button
                    onClick={() => handleToggleAllCustomers(!customerPortalSettings.allowAllCustomers)}
                    disabled={isUpdating}
                    className={`
                      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                      transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                      ${customerPortalSettings.allowAllCustomers ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}
                      ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    <span
                      className={`
                        pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                        transition duration-200 ease-in-out
                        ${customerPortalSettings.allowAllCustomers ? 'translate-x-5' : 'translate-x-0'}
                      `}
                    />
                  </button>
                </div>

                {/* Specific Customers */}
                {!customerPortalSettings.allowAllCustomers && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                    <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      Kiválasztott ügyfelek ({customerPortalSettings.allowedCustomers?.length || 0})
                    </label>
                    
                    {/* Search */}
                    <input
                      type="text"
                      placeholder="Keresés ügyfelek között..."
                      value={customerSearchTerm}
                      onChange={(e) => setCustomerSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 mb-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />

                    {/* Customer List */}
                    <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md">
                      {filteredCustomers.length === 0 ? (
                        <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                          Nincs találat
                        </div>
                      ) : (
                        filteredCustomers.map((customer) => (
                          <label
                            key={customer.id}
                            className="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                          >
                            <input
                              type="checkbox"
                              checked={customerPortalSettings.allowedCustomers?.includes(customer.id)}
                              onChange={() => handleToggleCustomer(customer.id)}
                              disabled={isUpdating}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span className="ml-3 text-sm text-gray-900 dark:text-gray-100">
                              {getCustomerName(customer)}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {t('common.close') || 'Close'}
          </button>
        </div>
      </div>
    </div>
    </Modal>
  );
};

export default ShareModal;
